import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import db, { auth } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, addDoc, getDocs, getDocFromServer, query, where } from 'firebase/firestore';
import { combineBarcode } from '../utils/barcodeUtils';
import { buildProductPhotos } from '../utils/productPhotos';
import { getCollectionName, isSimpleProductCountry, VALID_COUNTRY_IDS } from '../utils/countryConfig';
import { createProductSafely } from '../utils/productPersistence';
import { createVariantId } from '../utils/productVariants';
import { buildNewProduct } from '../utils/productEditor';
import ZxingBarcodeScanner from './ZxingBarcodeScanner';
import AddProductCamera from './AddProductCamera';
import NewProductPrep, { prepVariantIds } from './NewProductPrep';
import { addProductToList } from '../utils/livestreamLists';
import { readWithDeadline } from '../utils/productIdentityRead';
import { readCreationRecovery, writeCreationRecovery, clearCreationRecovery, parkSavedCreation, scrubCreationSelections, recoveryExpired, recoveryKey, withCreationLock } from '../utils/productCreationRecovery';
import '../styles.css';

function AddProduct() {
  const { country } = useParams();
  const [user, setUser] = useState(auth.currentUser);
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    let previous = auth.currentUser?.uid;
    const stop = onAuthStateChanged(auth, next => {
      if (previous && previous !== next?.uid) scrubCreationSelections(previous);
      previous = next?.uid; setUser(next);
    });
    return () => { stop(); if (previous !== auth.currentUser?.uid) scrubCreationSelections(previous); };
  }, []);
  if (!VALID_COUNTRY_IDS.includes(country)) return <p role="alert">無效的國家</p>;
  if (!user) return <p role="status">請先登入原保存帳號，再恢復商品。</p>;
  return <AddProductForm key={`${country}:${user.uid}:${generation}`} country={country} uid={user.uid} onStartNew={() => setGeneration(n => n + 1)} />;
}

function AddProductForm({ country, uid, onStartNew }) {
  const isCrystal = isSimpleProductCountry(country);
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    image: '',
    name: '',
    itemNumber: '',
    livePrice: '',
    onlinePrice: '',
    remarks: '',
    effects: '',
    costKoreanWon: '',
    costJPY: '',
    costTWD: '',
    costTHB: '',
    costRMB: '',
    weight: '',
    styles: [],
    styleIds: [],
    styleDetails: [],
    styleBarcodes: [],
    rawStyleBarcodes: [], // 原始條碼陣列
    photos: [],
    // 依國家提供預設幣別，切換幣別保留已輸入成本。
    costType: country === 'korea'
      ? 'KRW'
      : country === 'japan'
      ? 'JPY'
      : country === 'china'
      ? 'TWD'
      : country === 'thailand' ? 'THB' : 'TWD',
    newStyle: '',
    newStyleBarcode: new URLSearchParams(location.search).get('barcode') || '',
    vendor: ''
  });
  const [vendors, setVendors] = useState([]);
  const productIdRef = useRef(null);
  const pendingVariantIdRef = useRef(null);
  const [duplicates, setDuplicates] = useState([]);
  const [recoveredProduct, setRecoveredProduct] = useState(null);
  const [prepChoices, setPrepChoices] = useState([]);
  const [savedProduct, setSavedProduct] = useState(null);
  const savedProductRef = useRef(null);
  const [prepError, setPrepError] = useState('');
  const [appliedLists, setAppliedLists] = useState([]);
  const recordRef = useRef(null), aliveRef = useRef(true), lockRef = useRef(false);
  const [recoveryStatus, setRecoveryStatus] = useState('checking');
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [restored, setRestored] = useState(false);
  if (!pendingVariantIdRef.current) pendingVariantIdRef.current = createVariantId();
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({
    percent: 0,
    message: '',
  });
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isPhotoCameraOpen, setIsPhotoCameraOpen] = useState(true);
  const savingRef = useRef(false);
  const latestFormRef = useRef(formData);
  latestFormRef.current = formData;
  const [saveElapsed, setSaveElapsed] = useState(0);
  const reportSavePhase = useCallback(phase => {
    const messages = {
      authorization: '正在確認登入權限…',
      'previous-save': '正在確認先前是否已保存…',
      'identity-read': '正在讀取料號與條碼，檢查舊商品重複…',
      'identity-check': '正在比對商品識別碼…',
      transaction: '正在確認商品寫入狀態…',
      'identity-claims': '正在確認料號與條碼唯一性…',
      commit: '正在提交商品，等待伺服器確認…',
    };
    setSaveProgress({ message: messages[phase] || '正在保存商品…' });
  }, []);
  useEffect(() => {
    if (!isSaving) return undefined;
    const startedAt = Date.now();
    const timer = setInterval(() => setSaveElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [isSaving]);
  const [cameraDraft, setCameraDraft] = useState('');
  const nameInputRef = useRef(null);
  const assertOwner = () => {
    if (!aliveRef.current || auth.currentUser?.uid !== uid) throw new Error('帳號或頁面已切換，已停止後續操作；請回原帳號恢復同一商品');
  };
  const persistRecovery = (phase, product, choices) => {
    assertOwner();
    recordRef.current = writeCreationRecovery(uid, country, recordRef.current, {
      productId: productIdRef.current, phase,
      choices: choices.map(choice => ({ listId: choice.listId, selectedVariantIds: prepVariantIds(product, choice) })),
    });
  };
  const restore = useCallback(async () => {
    setRecoveryStatus('checking'); setRecoveryMessage('正在確認先前商品保存結果…');
    try {
      const record = readCreationRecovery(uid, country);
      recordRef.current = record;
      if (!record) { setRecoveryStatus('ready'); setRecoveryMessage(''); return; }
      if (record.phase === 'complete' || record.phase === 'saved') {
        await withCreationLock(uid, country, () => parkSavedCreation(uid, country, record));
        if (!aliveRef.current || auth.currentUser?.uid !== uid) return;
        recordRef.current = null; productIdRef.current = null;
        setRecoveryStatus('ready'); setRecoveryMessage(''); setRestored(false);
        setIsPhotoCameraOpen(true);
        return;
      }
      productIdRef.current = record.productId; setIsPhotoCameraOpen(false); setRestored(true);
      const snapshot = await readWithDeadline(() => getDocFromServer(doc(db, getCollectionName(country), record.productId)));
      if (!aliveRef.current || auth.currentUser?.uid !== uid) return;
      if (snapshot.exists()) {
        const product = { ...snapshot.data(), id: record.productId };
        if (product.country !== undefined && product.country !== country) throw new Error('商品國家不符，請確認資料');
        savedProductRef.current = product; setSavedProduct(product);
        const expired = recoveryExpired(record);
        recordRef.current = writeCreationRecovery(uid, country, record, { ...record, phase: record.phase === 'complete' ? 'complete' : 'saved', choices: expired ? [] : record.choices });
        setPrepChoices(expired ? [] : record.choices.map(choice => ({ ...choice, country, name: '備播清單', excluded: [] })));
        setRecoveryStatus('saved');
        setRecoveryMessage(expired ? '選款已過期，請重新設定；商品不會重建。' : '已找回商品，可開啟詳情或重試備播。');
      } else {
        setRecoveryStatus(record.phase === 'pending' ? 'missing-pending' : 'missing-saved');
        setRecoveryMessage(record.phase === 'pending' ? '目前查無商品。可沿用原 ID 重填，照片與表單須重填。' : '已保存商品目前不存在，已停止重試。');
      }
    } catch (error) {
      if (aliveRef.current && auth.currentUser?.uid === uid) { setRecoveryStatus('blocked'); setRecoveryMessage(`無法確認保存結果：${error.message}。請重新查證。`); setIsPhotoCameraOpen(false); }
    }
  }, [uid, country]);
  useEffect(() => {
    aliveRef.current = true; restore();
    const changed = event => {
      if (event.key === recoveryKey(uid, country) || event.key === null) { setRecoveryStatus('blocked'); setRecoveryMessage('另一分頁已更新保存紀錄，請重新查證後繼續。'); }
    };
    window.addEventListener('storage', changed);
    return () => { aliveRef.current = false; window.removeEventListener('storage', changed); };
  }, [restore, uid, country]);
  useEffect(() => {
    if (isPhotoCameraOpen) return undefined;
    const frame = window.requestAnimationFrame(() => {
      nameInputRef.current?.focus({ preventScroll: true });
      nameInputRef.current?.scrollIntoView?.({ block: 'center', behavior: 'auto' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isPhotoCameraOpen]);

  useEffect(() => {
    const fetchVendors = async () => {
      const vendorsRef = collection(db, 'vendors');
      const q = query(vendorsRef, where('country', '==', country));
      const snapshot = await getDocs(q);
      const vendorList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVendors(vendorList);
    };
    fetchVendors();
  }, [country]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'vendor') {
      localStorage.setItem('lastSelectedVendor', value.trim());
    }
  };

  // 根據 localStorage 中最後選擇的廠商排序，該廠商排第一
  const lastSelectedVendor = localStorage.getItem('lastSelectedVendor') || '';
  const sortedVendors = [...vendors].sort((a, b) => {
    if (a.name === lastSelectedVendor) return -1;
    if (b.name === lastSelectedVendor) return 1;
    return a.name.localeCompare(b.name);
  });

  const handleCostTypeChange = (e) => {
    const costType = e.target.value;
    setFormData(prev => ({ ...prev, costType }));
  };

  // 若輸入的廠商不存在則自動新增
  const addNewVendor = async (vendorName) => {
    if (!vendorName.trim()) return;
    const vendorsRef = collection(db, 'vendors');
    const q = query(vendorsRef, where('country', '==', country), where('name', '==', vendorName));
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      const docRef = await addDoc(vendorsRef, { name: vendorName, country });
      setVendors(prev => [...prev, { id: docRef.id, name: vendorName, country }]);
    }
  };

  const handleSaveUnlocked = async () => {
    if (savingRef.current) return;

    savingRef.current = true;
    setIsSaving(true);
    setSaveElapsed(0);
    setDuplicates([]);
    setRecoveredProduct(null);
    setPrepError('');
    if (!productIdRef.current) productIdRef.current = doc(collection(db, getCollectionName(country))).id;
    setSaveProgress({ percent: 10, message: '正在檢查商品資料...' });

    const resetSaving = () => {
      savingRef.current = false;
      setIsSaving(false);
      setSaveProgress({ percent: 0, message: '' });
    };

    try {
      persistRecovery(savedProductRef.current ? 'saved' : 'pending', savedProductRef.current || buildNewProduct(formData, pendingVariantIdRef.current), prepChoices);
    } catch (error) { setPrepError(error.message); resetSaving(); return; }

    // 如果使用者直接在欄位中輸入廠商（即 vendorInput 與 formData.vendor 同步），則在保存時自動檢查並新增
    if (!savedProductRef.current && !isCrystal && formData.vendor.trim() !== '') {
      setSaveProgress({ percent: 45, message: '正在確認廠商資料...' });
      const vendorName = formData.vendor.trim();
      const exists = vendors.find(v => v.name === vendorName);
      if (!exists) {
        try {
          await addNewVendor(vendorName);
        } catch (error) {
          console.error("新增廠商時發生錯誤:", error);
          alert("新增廠商時發生錯誤，請重試");
          resetSaving();
          return;
        }
      }
    }

    setSaveProgress({ percent: 65, message: '正在整理樣式與條碼...' });

    const newProduct = buildNewProduct(formData, pendingVariantIdRef.current);

    try {
      // Validate choices before creating a product, then let each existing list
      // transaction recheck live country/archive/variant state after creation.
      const targetProduct = savedProductRef.current || newProduct;
      if (prepChoices.some(choice => choice.country !== country)) throw new Error('備播清單國家不符，請重新設定');
      if (prepChoices.some(choice => targetProduct.styleIds?.length && !prepVariantIds(targetProduct, choice).length)) throw new Error('請至少選一個備播款式，或取消套用該清單');
      const wasSaved = Boolean(savedProductRef.current);
      assertOwner();
      const saved = savedProductRef.current || await createProductSafely({ country, product: newProduct, id: productIdRef.current, onProgress: reportSavePhase });
      assertOwner();
      savedProductRef.current = saved;
      setSavedProduct(saved);
      persistRecovery('saved', saved, prepChoices);
      if (!wasSaved && JSON.stringify(buildNewProduct(latestFormRef.current, pendingVariantIdRef.current)) !== JSON.stringify(newProduct)) {
        setRecoveredProduct(saved);
        resetSaving();
        return;
      }
      let remaining = [...prepChoices];
      for (const choice of prepChoices) {
        assertOwner();
        setSaveProgress({ message: `商品已保存，正在套用「${choice.name}」…` });
        await addProductToList({ listId: choice.listId, country, product: saved, selectedVariantIds: prepVariantIds(saved, choice) });
        assertOwner();
        remaining = remaining.filter(c => c.listId !== choice.listId);
        persistRecovery('saved', saved, remaining);
        setPrepChoices(remaining);
        setAppliedLists(names => [...names, choice.name]);
      }
      persistRecovery('complete', saved, []);
      parkSavedCreation(uid, country, recordRef.current);
      recordRef.current = null;
      setSaveProgress({ percent: 100, message: '保存完成，正在開啟商品頁...' });
      setIsPhotoCameraOpen(false);
      navigate(`/view/${country}/${saved.id}`);
    } catch (error) {
      if (!aliveRef.current || auth.currentUser?.uid !== uid) return;
      console.error("Error adding document: ", error);
      if (error.code === 'product-recovered') {
        savedProductRef.current = error.current;
        setSavedProduct(error.current);
        setRecoveredProduct(error.current);
        // A late previous commit can contain different variants from a newly
        // re-entered form. Preserve exact intent; require explicit reselection
        // instead of accidentally default-selecting all of the older variants.
        const pendingChoices = prepChoices.map(choice => ({ ...choice, selectedVariantIds: prepVariantIds(newProduct, choice) }));
        setPrepChoices(pendingChoices);
        try { persistRecovery('saved', error.current, pendingChoices); } catch (storageError) { setPrepError(storageError.message); }
      } else if (savedProductRef.current) {
        setPrepError(error.message || '請稍後重試');
      } else if (error.code === 'product-duplicate') {
        setDuplicates(error.matches || []);
      } else {
        alert(`保存商品失敗：${error.message || '請稍後重試'}\n您的輸入仍保留，重試不會重複新增商品。`);
      }
      resetSaving();
    }
  };

  const handleSave = async () => {
    if (lockRef.current || !['ready', 'saved'].includes(recoveryStatus)) return;
    lockRef.current = true;
    try { await withCreationLock(uid, country, handleSaveUnlocked); }
    catch (error) { if (aliveRef.current) setPrepError(error.message); }
    finally { lockRef.current = false; }
  };
  const openSavedProduct = async () => {
    if (savingRef.current || recoveredProduct) return;
    try {
      await withCreationLock(uid, country, () => {
        assertOwner();
        if (recordRef.current) parkSavedCreation(uid, country, recordRef.current);
        recordRef.current = null;
        navigate(`/view/${country}/${savedProduct.id}`);
      });
    } catch (error) { setPrepError(error.message); }
  };
  const changePrepChoices = choices => {
    try {
      if (savedProductRef.current) persistRecovery('saved', savedProductRef.current, choices);
      setPrepChoices(choices); setPrepError('');
    } catch (error) { setPrepError(error.message); }
  };
  const startNew = async () => {
    if (!savedProductRef.current || savingRef.current || lockRef.current) return;
    try { await withCreationLock(uid, country, () => { assertOwner(); if (recordRef.current) parkSavedCreation(uid, country, recordRef.current); recordRef.current = null; onStartNew(); }); }
    catch (error) { setPrepError(error.message); }
  };
  const finishMissingRecovery = async () => {
    if (recoveryStatus !== 'missing-saved' || savingRef.current) return;
    try {
      await withCreationLock(uid, country, () => {
        assertOwner(); clearCreationRecovery(uid, country, recordRef.current);
        navigate(`/products/${country}`);
      });
    } catch (error) { setPrepError(error.message); }
  };

  const addStyle = () => {
    const { newStyle, newStyleBarcode } = formData;
    if (newStyleBarcode.trim() !== '' || newStyle.trim() !== '') {
      const rawBarcode = newStyleBarcode.trim();
      const combinedBarcode = combineBarcode(rawBarcode, formData.itemNumber);
      const styleId = pendingVariantIdRef.current;
      pendingVariantIdRef.current = createVariantId();
      setFormData(prev => ({
        ...prev,
        styleIds: [...prev.styleIds, styleId],
        styleDetails: [...prev.styleDetails, { active: true }],
        styles: [...prev.styles, newStyle.trim() || '無樣式'],
        styleBarcodes: [...prev.styleBarcodes, combinedBarcode], // 組合條碼
        rawStyleBarcodes: [...(prev.rawStyleBarcodes || []), rawBarcode], // 原始條碼
        newStyle: '',
        newStyleBarcode: ''
      }));
    }
  };

  const handleBarcodeScan = (barcode) => {
    setFormData(prev => ({ ...prev, newStyleBarcode: barcode.trim() }));
    setIsScannerOpen(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const url = reader.result;
      setFormData(prev => ({
        ...prev,
        photos: buildProductPhotos({
          photos: [
            ...(prev.photos || []),
            { url, source: 'upload', label: '商品照片', createdAt: new Date().toISOString() },
          ],
        }),
      }));
    };
    reader.readAsDataURL(file);
  };

  const pageStyle = {
    minHeight: '100vh',
    background: '#f8f9fa',
    padding: '1rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    maxWidth: '480px',
    margin: '0 auto'
  };

  const formGroupStyle = {
    marginBottom: '1.25rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#666',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  };

  const buttonStyle = {
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.2s'
  };

  const progressBoxStyle = {
    background: '#f7f8fb',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '0.85rem',
    marginBottom: '1rem'
  };

  const scannerButtonStyle = {
    background: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #dbeafe',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  };

  const scannerPanelStyle = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '0.75rem',
    marginBottom: '0.75rem'
  };
  const recoveryButtonStyle = { ...scannerButtonStyle, background: '#f5f5f5', color: '#333', borderColor: '#ddd', whiteSpace: 'normal' };
  const saveDisabled = isSaving || !['ready', 'saved'].includes(recoveryStatus);

  return (
    <div style={pageStyle}>
      <div style={cardStyle} aria-hidden={isPhotoCameraOpen || undefined} inert={isPhotoCameraOpen ? '' : undefined}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: '#333', textAlign: 'center' }}>
          {isCrystal ? '新增水晶商品' : '新增商品'}
        </h2>
        {recoveryMessage && <div role="status" style={progressBoxStyle}>
          <p>{recoveryMessage}</p>
          {recordRef.current && !savedProduct && <p style={{ overflowWrap: 'anywhere' }}>商品識別：{recordRef.current.productId}</p>}
          {['blocked', 'missing-pending', 'missing-saved'].includes(recoveryStatus) && <button type="button" style={recoveryButtonStyle} disabled={isSaving} onClick={restore}>重新查證保存結果</button>}
          {recoveryStatus === 'missing-pending' && <button type="button" style={recoveryButtonStyle} onClick={() => { setRecoveryStatus('ready'); setRestored(false); setPrepChoices([]); setRecoveryMessage('沿用原 ID；請重填資料與備播設定。'); setIsPhotoCameraOpen(true); }}>沿用原商品 ID 重新填寫</button>}
          {recoveryStatus === 'missing-saved' && <><p><a href={`/products/${country}`}>前往商品列表確認</a></p><button type="button" style={recoveryButtonStyle} onClick={finishMissingRecovery}>結束恢復，返回商品列表</button><p>不新增或刪除商品。</p></>}
        </div>}
        {prepError && !savedProduct && <p role="alert">{prepError}</p>}
        <fieldset disabled={Boolean(savedProduct) || recoveryStatus !== 'ready'} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: restored ? 'none' : undefined }}>
        {/* 圖片上傳 */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>商品圖</label>
          {!isPhotoCameraOpen && <button type="button" style={{ ...scannerButtonStyle, width: '100%', marginBottom: '0.75rem' }} onClick={() => { setIsScannerOpen(false); setIsPhotoCameraOpen(true); }}>{cameraDraft ? '確認剛才拍的照片' : '開啟相機拍照'}</button>}
          <input type="file" accept="image/*" onChange={handleImageUpload} style={inputStyle} />
          <small style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem', display: 'block' }}>
            前台新增只會加入商品圖，不會覆蓋商品主圖片；商品主圖片請用檔案批次匯入。
          </small>
          {(formData.photos || []).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '0.5rem', marginTop: '0.75rem' }}>
              {formData.photos.map((photo, index) => (
                <img
                  key={`${photo.url}-${index}`}
                  src={photo.url}
                  alt={`商品圖 ${index + 1}`}
                  style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '8px' }}
                />
              ))}
            </div>
          )}
        </div>
        {/* 商品基本資訊 */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>名稱</label>
          <input ref={nameInputRef} type="text" name="name" value={formData.name} onChange={handleChange} style={inputStyle} />
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>料號</label>
          <input type="text" name="itemNumber" value={formData.itemNumber} onChange={handleChange} style={inputStyle} />
        </div>
        {!isCrystal && (
          <>
            <div style={formGroupStyle}>
              <label style={labelStyle}>直播價</label>
              <input type="text" name="livePrice" value={formData.livePrice} onChange={handleChange} style={inputStyle} />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>商城價</label>
              <input type="text" name="onlinePrice" value={formData.onlinePrice} onChange={handleChange} style={inputStyle} />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>備註</label>
              <input type="text" name="remarks" value={formData.remarks} onChange={handleChange} style={inputStyle} />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>成本類型</label>
              <select name="costType" value={formData.costType} onChange={handleCostTypeChange} style={inputStyle}>
                <option value="TWD">台幣</option>
                {country === 'korea' && <option value="KRW">韓幣</option>}
                {country === 'japan' && <option value="JPY">日幣</option>}
                {country === 'thailand' && <option value="THB">泰銖</option>}
                {country === 'china' && <option value="RMB">人民幣</option>}
              </select>
            </div>
            {formData.costType === 'TWD' && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>成本 (台幣)</label>
                <input type="text" name="costTWD" value={formData.costTWD} onChange={handleChange} style={inputStyle} />
              </div>
            )}
            {formData.costType === 'KRW' && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>成本 (韓幣)</label>
                <input type="text" name="costKoreanWon" value={formData.costKoreanWon} onChange={handleChange} style={inputStyle} />
              </div>
            )}
            {formData.costType === 'JPY' && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>成本 (日幣)</label>
                <input type="text" name="costJPY" value={formData.costJPY} onChange={handleChange} style={inputStyle} />
              </div>
            )}
            {formData.costType === 'THB' && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>成本 (泰銖)</label>
                <input type="text" name="costTHB" value={formData.costTHB} onChange={handleChange} style={inputStyle} />
              </div>
            )}
            {formData.costType === 'RMB' && (
              <div style={formGroupStyle}>
                <label style={labelStyle}>成本 (人民幣)</label>
                <input type="text" name="costRMB" value={formData.costRMB} onChange={handleChange} style={inputStyle} />
              </div>
            )}
            <div style={formGroupStyle}>
              <label style={labelStyle}>重量</label>
              <input type="text" name="weight" value={formData.weight} onChange={handleChange} style={inputStyle} />
            </div>
          </>
        )}
        <div style={formGroupStyle}>
          <label style={labelStyle}>新增樣式</label>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input type="text" name="newStyle" value={formData.newStyle} onChange={handleChange} placeholder="樣式名稱" style={{ ...inputStyle, flex: 1 }} />
            {!isCrystal && (
              <input type="text" name="newStyleBarcode" value={formData.newStyleBarcode} onChange={handleChange} placeholder="樣式條碼" style={{ ...inputStyle, flex: 1 }} />
            )}
          </div>
          {!isCrystal && (
            <>
              <button
                type="button"
                onClick={() => { setIsPhotoCameraOpen(false); setIsScannerOpen((prev) => !prev); }}
                style={{ ...scannerButtonStyle, width: '100%', marginBottom: '0.75rem' }}
              >
                {isScannerOpen ? '關閉掃描' : '手機掃條碼'}
              </button>
              {isScannerOpen && (
                <div style={scannerPanelStyle}>
                  <ZxingBarcodeScanner scanning={isScannerOpen} onScan={handleBarcodeScan} />
                </div>
              )}
            </>
          )}
          <button 
            onClick={addStyle}
            style={{
              background: '#f5f5f5',
              color: '#333',
              border: '1px solid #e5e5e5',
              borderRadius: '8px',
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => { e.target.style.background = '#eee'; }}
            onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
          >
            新增樣式
          </button>
        </div>
        {formData.styles.length > 0 && (
          <div style={formGroupStyle}>
            <label style={labelStyle}>樣式列表</label>
            <div style={{ background: '#f8f9fa', padding: '0.75rem', borderRadius: '8px' }}>
              {formData.styles.map((style, index) => {
                // 顯示原始條碼（不顯示料號）
                const displayBarcode = formData.rawStyleBarcodes?.[index] || formData.styleBarcodes[index] || '';
                return (
                  <div key={formData.styleIds[index]} style={{ padding: '0.5rem 0', borderBottom: index < formData.styles.length - 1 ? '1px solid #e5e5e5' : 'none' }}>
                    {style} ({displayBarcode})
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {!isCrystal && (
          <div style={formGroupStyle}>
            <label style={labelStyle}>廠商</label>
            <input 
              type="text" 
              name="vendor" 
              value={formData.vendor} 
              onChange={handleChange} 
              list="vendorList" 
              placeholder="選擇或輸入廠商" 
              style={inputStyle}
            />
            <datalist id="vendorList">
              {sortedVendors.map(vendor => (
                <option key={vendor.id} value={vendor.name} />
              ))}
            </datalist>
            <small style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.25rem', display: 'block' }}>若廠商不存在，直接輸入後保存會自動新增</small>
          </div>
        )}
        </fieldset>
        {['ready', 'saved'].includes(recoveryStatus) && <NewProductPrep country={country} product={savedProduct || buildNewProduct(formData, pendingVariantIdRef.current)} choices={prepChoices} onChange={changePrepChoices} busy={isSaving} saved={Boolean(savedProduct)} />}
        {savedProduct && <div role="status" style={progressBoxStyle}>
          <p>商品已保存{prepChoices.length ? '／備播待套用' : ''}。</p>
          <p style={{ overflowWrap: 'anywhere' }}>{savedProduct.name || '未填品名'} · 商品識別：{savedProduct.id}</p>
          {appliedLists.length > 0 && <p>已套用：{appliedLists.join('、')}</p>}
          {prepError && <p role="alert">備播待重試：{prepError}</p>}
          {!recoveredProduct && <button type="button" style={recoveryButtonStyle} disabled={isSaving} onClick={openSavedProduct}>查看已儲存商品</button>}
          {!recoveredProduct && !isSaving && ['ready', 'saved'].includes(recoveryStatus) && <p><button type="button" style={recoveryButtonStyle} onClick={startNew}>新增另一筆商品</button></p>}
        </div>}
        {recoveredProduct && (
          <div role="alert" style={{ padding: '1rem', marginBottom: '1rem', border: '1px solid #60a5fa', borderRadius: '8px', background: '#eff6ff' }}>
            <p>商品已保存，沒有建立第二筆商品。後續修改尚未套用。</p>

            <button type="button" style={{ display: 'block', marginTop: '0.75rem', padding: '0.5rem' }} onClick={() => {
              if (window.confirm('使用已儲存商品會離開此表單，尚未套用的新修改不會帶入。確定繼續？')) navigate(`/edit/${country}/${recoveredProduct.id}`);
            }}>使用已儲存商品</button>
          </div>
        )}
        {duplicates.length > 0 && (
          <div role="alert" style={{ padding: '1rem', marginBottom: '1rem', border: '1px solid #fb923c', borderRadius: '8px', background: '#fff7ed' }}>
            <p>此料號或條碼已有商品，沒有重複建立。您填寫的資料仍保留。</p>
            {duplicates.map(match => (
              <button type="button" key={match.id} onClick={() => navigate(`/view/${country}/${match.id}`)} style={{ margin: '0.25rem', padding: '0.5rem' }}>
                查看現有商品：{match.name || match.itemNumber || match.id}
              </button>
            ))}
          </div>
        )}
        {isSaving && (
          <div role="status" aria-live="polite" style={progressBoxStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#374151' }}>
              <span>{saveProgress.message}</span>
              <span>已等待 {saveElapsed} 秒</span>
            </div>
            {saveElapsed >= 15 && <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>仍在等待確認，請保留此頁。</p>}
          </div>
        )}
        <button 
          onClick={handleSave}
          disabled={saveDisabled}
          style={{
            ...buttonStyle,
            opacity: saveDisabled ? 0.65 : 1,
            cursor: isSaving ? 'wait' : saveDisabled ? 'default' : 'pointer',
          }}
          onMouseOver={(e) => { e.target.style.background = '#555'; }}
          onMouseOut={(e) => { e.target.style.background = '#333'; }}
        >
          {isSaving ? '保存中...' : savedProduct ? (prepChoices.length ? '重試套用備播' : '開啟已保存商品') : '保存'}
        </button>
      </div>
      {isPhotoCameraOpen && recoveryStatus === 'ready' && <AddProductCamera initialPhoto={cameraDraft} onDraftChange={setCameraDraft} onSkip={() => setIsPhotoCameraOpen(false)} onConfirm={url => {
        setFormData(prev => ({ ...prev, photos: buildProductPhotos({ photos: [...(prev.photos || []), { url, source: 'upload', label: '商品照片', createdAt: new Date().toISOString() }] }) }));
        setIsPhotoCameraOpen(false);
        setCameraDraft('');
      }} />}
    </div>
  );
}

export default AddProduct;
